import { useTranslation } from 'react-i18next';

import { Report } from 'types';
import {
  useExtensionRequestsColumns,
  useLateResponseColumns,
  useOutstandingResponseColumns,
  useRespondedOnTimeColumns,
  useRespondedYearToDateColumns,
} from '../components';

import { useThemedValueSwitch } from '@tq/ui-components';

export enum reports {
  annualReport = 'annualReport',
  annualDfi = 'annualDfi',
  extensionRequests = 'extensionRequests',
  lateResponses = 'lateResponses',
  outstandingResponses = 'outstandingResponses',
  respondedOnTime = 'respondedOnTime',
  respondedYearToDate = 'respondedYearToDate',
}

export const useReports = (): Report[] => {
  const { t } = useTranslation('reports');
  const { isTQ } = useThemedValueSwitch();

  const annualDfiReport: Report = {
    name: isTQ ? reports.annualReport : reports.annualDfi,
    header: isTQ
      ? t('tqen_annual_report', 'Annual report')
      : t('annual_dfi_report', 'Annual DfI report'),
    description: isTQ
      ? t(
          'tqen_annual_report_description',
          'View the annual report and submit your commentary.'
        )
      : t(
          'annual_dfi_report_description',
          'View the annual DfI report and submit your commentary.'
        ),
    columns: [],
    hasDateFilter: true,
    visibleOnMobile: false,
  };

  const extensionRequestsReport: Report = {
    name: reports.extensionRequests,
    header: t('extension_requests_report', 'Extension requests report'),
    description: t(
      'extension_requests_report_description',
      'View a report of consultations where an extension to the original deadline was requested.'
    ),
    columns: useExtensionRequestsColumns(),
    hasDateFilter: true,
    visibleOnMobile: true,
  };

  const lateResponsesReport: Report = {
    name: reports.lateResponses,
    header: t('late_response_report', 'Late responses report'),
    description: t(
      'late_response_report_description',
      'View a report of consultations that were responded to after the agreed deadline.'
    ),
    columns: useLateResponseColumns(),
    hasDateFilter: true,
    visibleOnMobile: true,
  };

  const outstandingResponsesReport: Report = {
    name: reports.outstandingResponses,
    header: t('outstanding_responses_report', 'Outstanding responses report'),
    description: t(
      'outstanding_responses_report_description',
      'View a report of consultations with an outstanding response.'
    ),
    columns: useOutstandingResponseColumns(),
    hasDateFilter: true,
    visibleOnMobile: true,
  };

  const respondedOnTimeReport: Report = {
    name: reports.respondedOnTime,
    header: t('responded_on_time_report', 'Responded on time report'),
    description: t(
      'responded_on_time_report_description',
      'View a report of consultations responded to within the agreed deadline.'
    ),
    columns: useRespondedOnTimeColumns(),
    hasDateFilter: true,
    visibleOnMobile: true,
  };

  const respondedYearToDateReport: Report = {
    name: reports.respondedYearToDate,
    header: t('responded_year_to_date_report', 'Responded - year to date report'),
    description: t(
      'responded_year_to_date_report_description',
      'View a report of all consultations that have been responded to financial year to date.'
    ),
    columns: useRespondedYearToDateColumns(),
    hasDateFilter: false,
    visibleOnMobile: true,
  };

  return [
    annualDfiReport,
    extensionRequestsReport,
    lateResponsesReport,
    outstandingResponsesReport,
    respondedOnTimeReport,
    respondedYearToDateReport,
  ];
};
